import React, { useEffect, useState } from "react";

const API = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const card = "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";
const input = "w-full bg-black border border-[#00ff90]/20 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[#00ff90]/50";
const label = "text-xs text-[#00ff90]/60 uppercase tracking-wider block mb-1.5";
const btn   = "text-xs px-3 py-2 rounded-lg border border-[#00ff90]/30 bg-[#00ff90]/10 text-[#00ff90] hover:bg-[#00ff90]/20 transition-colors";
const btnAlt = "text-xs px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-white/70 hover:bg-white/10 transition-colors";

const authH = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

export default function SSOSettingsPanel() {
  const [cfg, setCfg]           = useState(null);
  const [form, setForm]         = useState({
    provider: "google", domain: "", client_id: "", client_secret: "",
    auto_provision: true, auto_deprovision: false, enabled: false,
  });
  const [log, setLog]           = useState([]);
  const [busy, setBusy]         = useState(null);
  const [message, setMessage]   = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/integrations/sso/config`, { headers: authH() });
      const data = await r.json();
      setCfg(data);
      setForm(f => ({
        ...f,
        provider:         data.provider         || f.provider,
        domain:           data.domain           || "",
        client_id:        data.client_id        || "",
        auto_provision:   data.auto_provision   ?? true,
        auto_deprovision: data.auto_deprovision ?? false,
        enabled:          data.enabled          ?? false,
      }));
    } catch {}
    try {
      const r = await fetch(`${API}/api/integrations/sso/sync-log`, { headers: authH() });
      const data = await r.json();
      setLog(Array.isArray(data) ? data : []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy("save"); setMessage(null);
    try {
      const body = { ...form };
      if (!body.client_secret) delete body.client_secret;
      const r = await fetch(`${API}/api/integrations/sso/config`, {
        method: "POST", headers: authH(), body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Save failed");
      setMessage({ ok: true, text: "Saved" });
      await load();
    } catch (e) { setMessage({ ok: false, text: e.message }); }
    finally { setBusy(null); }
  };

  const testConn = async () => {
    setBusy("test"); setMessage(null);
    try {
      const r = await fetch(`${API}/api/integrations/sso/test`, { method: "POST", headers: authH() });
      const data = await r.json();
      setMessage({ ok: data.ok, text: data.message + ` (${data.user_count ?? 0} users)` });
    } catch (e) { setMessage({ ok: false, text: e.message }); }
    finally { setBusy(null); }
  };

  const syncNow = async () => {
    setBusy("sync"); setMessage(null);
    try {
      const r = await fetch(`${API}/api/integrations/sso/sync`, { method: "POST", headers: authH() });
      const data = await r.json();
      setMessage({ ok: data.ok, text:
        `${data.created} created · ${data.updated} updated · ${data.deactivated} deactivated`
        + (data.errors?.length ? ` · ${data.errors.length} errors` : "") });
      await load();
    } catch (e) { setMessage({ ok: false, text: e.message }); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-5">
      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <h3 className="text-sm font-medium text-[#00ff90]">SSO &amp; Directory Sync</h3>
          <p className="text-xs text-white/40 mt-0.5">
            Google Workspace or Microsoft 365 single sign-on + directory sync. Credentials are stored encrypted.
          </p>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={label}>Provider</label>
              <select className={input}
                value={form.provider}
                onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}>
                <option value="google">Google Workspace</option>
                <option value="microsoft">Microsoft 365</option>
              </select>
            </div>
            <div>
              <label className={label}>Domain</label>
              <input className={input} placeholder="acmecorp.com"
                value={form.domain}
                onChange={e => setForm(f => ({ ...f, domain: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className={label}>Client ID</label>
              <input className={input}
                value={form.client_id}
                onChange={e => setForm(f => ({ ...f, client_id: e.target.value }))} />
            </div>
            <div className="col-span-2">
              <label className={label}>Client Secret {cfg?.has_secret && <span className="text-white/40">(leave blank to keep)</span>}</label>
              <input className={input} type="password" placeholder={cfg?.has_secret ? "•••••••• (stored)" : ""}
                value={form.client_secret}
                onChange={e => setForm(f => ({ ...f, client_secret: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 text-xs text-white/70">
              <input type="checkbox" checked={form.auto_provision}
                onChange={e => setForm(f => ({ ...f, auto_provision: e.target.checked }))} />
              Auto-provision new users
            </label>
            <label className="flex items-center gap-2 text-xs text-white/70">
              <input type="checkbox" checked={form.auto_deprovision}
                onChange={e => setForm(f => ({ ...f, auto_deprovision: e.target.checked }))} />
              Auto-deprovision removed users
            </label>
            <label className="flex items-center gap-2 text-xs text-white/70 col-span-2">
              <input type="checkbox" checked={form.enabled}
                onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))} />
              SSO enabled for this org
            </label>
          </div>

          <div className="flex gap-2 flex-wrap pt-2">
            <button className={btn}    disabled={busy} onClick={save}>{busy === "save" ? "Saving…" : "Save"}</button>
            <button className={btnAlt} disabled={busy} onClick={testConn}>{busy === "test" ? "Testing…" : "Test Connection"}</button>
            <button className={btnAlt} disabled={busy} onClick={syncNow}>{busy === "sync" ? "Syncing…" : "Sync Now"}</button>
          </div>
          {message && (
            <p className={`text-xs ${message.ok ? "text-[#00ff90]" : "text-red-400"}`}>{message.text}</p>
          )}
          {cfg?.last_sync_at && (
            <p className="text-xs text-white/40">Last sync: {new Date(cfg.last_sync_at).toLocaleString()}</p>
          )}
        </div>
      </div>

      <div className={card}>
        <div className="px-5 py-4 border-b border-white/5">
          <h3 className="text-sm font-medium text-[#00ff90]/80">Recent sync log</h3>
        </div>
        <div className="p-3">
          {log.length === 0 ? (
            <p className="text-white/30 text-sm italic p-3">No syncs yet.</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-white/40 uppercase">
                <tr>
                  <th className="text-left p-2">Started</th>
                  <th className="text-left p-2">Type</th>
                  <th className="text-right p-2">Created</th>
                  <th className="text-right p-2">Updated</th>
                  <th className="text-right p-2">Deactivated</th>
                  <th className="text-right p-2">Errors</th>
                </tr>
              </thead>
              <tbody>
                {log.slice(0, 5).map(r => (
                  <tr key={r.id} className="border-t border-white/5">
                    <td className="p-2 text-white/70">{r.started_at ? new Date(r.started_at).toLocaleString() : "—"}</td>
                    <td className="p-2 text-white/60">{r.sync_type}</td>
                    <td className="p-2 text-right text-[#00ff90]">{r.users_created}</td>
                    <td className="p-2 text-right text-blue-400">{r.users_updated}</td>
                    <td className="p-2 text-right text-yellow-400">{r.users_deactivated}</td>
                    <td className="p-2 text-right text-red-400">{r.errors?.length || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
